/**
 * How many contacts leave an outreach job before a mail is written/sent.
 * Mirror of functions/helpers/outreachDropout.js — keep the two in sync.
 */

export const DROPOUT_MIN_SAMPLE = 8;
export const DROPOUT_OK_PCT = 15;
export const DROPOUT_WATCH_PCT = 40;
export const DROPOUT_DAILY_CAP_MULT = 4;

const asCount = (value) => Math.max(0, Number(value) || 0);

export const dailyAttemptCap = (dailyLimit) =>
  Math.max(1, asCount(dailyLimit)) * DROPOUT_DAILY_CAP_MULT;

/**
 * How many contacts already used today's 4× attempt budget.
 * Mirror of functions/helpers/outreachDropout.js — keep the two in sync.
 */
export const countYieldAttempts = ({
  researchedToday = 0,
  droppedToday = 0,
  passedToday = 0,
  hasResearch = false,
} = {}) => {
  const droppedPlusPassed = asCount(droppedToday) + asCount(passedToday);
  if (!hasResearch) return droppedPlusPassed;
  return Math.max(asCount(researchedToday), droppedPlusPassed);
};

export const yieldBudgetOpen = ({ passedToday = 0, attemptedToday = 0, dailyLimit } = {}) => {
  const limit = Math.max(1, asCount(dailyLimit));
  if (asCount(passedToday) >= limit) return false;
  if (asCount(attemptedToday) >= dailyAttemptCap(limit)) return false;
  return true;
};

const resolvedDropped = (stats = {}) => {
  if (stats.dropped != null && Number.isFinite(Number(stats.dropped))) {
    return asCount(stats.dropped);
  }
  return asCount(stats.failed) + asCount(stats.failed_research);
};

export const dropoutCounts = (stats = {}, { isPrepare = false } = {}) => {
  const dropped = resolvedDropped(stats);
  const passed = isPrepare ? asCount(stats.prepared) : asCount(stats.sent);
  return { dropped, passed, attempted: dropped + passed };
};

const dropoutHealth = (pct) => {
  if (pct == null) return null;
  if (pct > DROPOUT_WATCH_PCT) return 'bad';
  if (pct > DROPOUT_OK_PCT) return 'watch';
  return 'ok';
};

export const dropoutRate = (stats = {}, opts = {}) => {
  const counts = dropoutCounts(stats, opts);
  if (counts.attempted <= 0) {
    return { ...counts, pct: null, health: null, ready: false };
  }
  const pct = Math.round((counts.dropped / counts.attempted) * 100);
  return {
    ...counts,
    pct,
    health: dropoutHealth(pct),
    ready: counts.attempted >= DROPOUT_MIN_SAMPLE,
  };
};

export const dropoutPillTitle = (rate) => {
  if (!rate || rate.pct == null) return '';
  return `${rate.dropped} of ${rate.attempted} contacts left the flow before a mail (no email, filter, research). The agent keeps pulling replacements the same day, up to 4× the daily pace, so the mail count still fills.`;
};
